import type { EChartsOption } from 'echarts'

export type AnalyticsThemeMode = 'light' | 'dark'

const LIGHT = {
 primary: '#2563EB',
 secondary: '#14B8A6',
 muted: '#94A3B8',
 grid: 'rgba(148, 163, 184, 0.18)',
 label: '#64748B',
 axisText: '#334155',
 tooltipBg: '#FFFFFF',
 tooltipBorder: '#E2E8F0',
 tooltipText: '#0F172A',
 zeroBar: '#E8EDF3',
 negativeBar: '#DC2626',
 palette: ['#2563EB', '#14B8A6', '#F59E0B', '#64748B', '#1E293B', '#60A5FA'],
} as const

const DARK = {
 primary: '#3B82F6',
 secondary: '#2DD4BF',
 muted: '#6F7C91',
 grid: 'rgba(148, 163, 184, 0.1)',
 label: '#A8B3C5',
 axisText: '#A8B3C5',
 tooltipBg: '#141E30',
 tooltipBorder: 'rgba(148, 163, 184, 0.22)',
 tooltipText: '#F8FAFC',
 zeroBar: '#18243A',
 negativeBar: '#EF4444',
 palette: ['#3B82F6', '#22C55E', '#F59E0B', '#A8B3C5', '#60A5FA', '#2DD4BF'],
} as const

export function getAnalyticsColors(mode: AnalyticsThemeMode) {
 return mode === 'dark' ? DARK : LIGHT
}

/** ECharts SVG/canvas does not resolve CSS `inherit` — use the app body font stack. */
export const ANALYTICS_FONT_STACK = "'Tajawal', ui-sans-serif, system-ui, sans-serif"

export function getAnalyticsFontFamily(): string {
 if (typeof document === 'undefined') return ANALYTICS_FONT_STACK
 const bodyFont = getComputedStyle(document.body).fontFamily?.trim()
 if (bodyFont && bodyFont !== 'inherit') return bodyFont
 return ANALYTICS_FONT_STACK
}

function withFontStyle(existing: unknown, fontFamily: string): { fontFamily: string } {
 if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
  return { fontFamily, ...(existing as Record<string, unknown>) }
 }
 return { fontFamily }
}

function mapAxisFont(axis: unknown, fontFamily: string): unknown {
 if (!axis) return axis
 if (Array.isArray(axis)) return axis.map((item) => mapAxisFontItem(item, fontFamily))
 if (typeof axis === 'object') return mapAxisFontItem(axis as Record<string, unknown>, fontFamily)
 return axis
}

function mapAxisFontItem(axis: Record<string, unknown>, fontFamily: string) {
 return {
  ...axis,
  axisLabel: withFontStyle(axis.axisLabel, fontFamily),
  nameTextStyle: withFontStyle(axis.nameTextStyle, fontFamily),
 }
}

function mapLegendFont(legend: unknown, fontFamily: string): unknown {
 if (!legend) return legend
 if (Array.isArray(legend)) return legend.map((item) => mapLegendFontItem(item, fontFamily))
 if (typeof legend === 'object') return mapLegendFontItem(legend as Record<string, unknown>, fontFamily)
 return legend
}

function mapLegendFontItem(legend: Record<string, unknown>, fontFamily: string) {
 return {
  ...legend,
  textStyle: withFontStyle(legend.textStyle, fontFamily),
 }
}

function mapTooltipFont(tooltip: unknown, fontFamily: string): unknown {
 if (!tooltip || typeof tooltip !== 'object' || Array.isArray(tooltip)) return tooltip
 return {
  ...(tooltip as Record<string, unknown>),
  textStyle: withFontStyle((tooltip as Record<string, unknown>).textStyle, fontFamily),
 }
}

/** Inject resolved app font into all ECharts text surfaces. */
export function applyAnalyticsFont(option: EChartsOption): EChartsOption {
 const fontFamily = getAnalyticsFontFamily()
 return {
  ...option,
  textStyle: withFontStyle(option.textStyle, fontFamily),
  legend: mapLegendFont(option.legend, fontFamily) as EChartsOption['legend'],
  tooltip: mapTooltipFont(option.tooltip, fontFamily) as EChartsOption['tooltip'],
  xAxis: mapAxisFont(option.xAxis, fontFamily) as EChartsOption['xAxis'],
  yAxis: mapAxisFont(option.yAxis, fontFamily) as EChartsOption['yAxis'],
 }
}

export function buildBaseChartOption(mode: AnalyticsThemeMode, rtl: boolean): EChartsOption {
 const c = getAnalyticsColors(mode)
 const fontFamily = getAnalyticsFontFamily()
 return {
  backgroundColor: 'transparent',
  textStyle: { fontFamily, color: c.label },
  color: [...c.palette],
  grid: {
   left: rtl ? 16 : 56,
   right: rtl ? 56 : 16,
   top: 24,
   bottom: 32,
   containLabel: false,
  },
  xAxis: {
   ...(rtl ? { inverse: true } : {}),
   axisLine: { show: false },
   axisTick: { show: false },
   axisLabel: { fontFamily, color: c.label, fontSize: 11, interval: 'auto' },
  },
  yAxis: {
   ...(rtl ? { position: 'right' as const } : {}),
   axisLine: { show: false },
   axisTick: { show: false },
   axisLabel: { fontFamily, color: c.label, fontSize: 11 },
   splitLine: { show: false },
  },
  tooltip: {
   confine: true,
   backgroundColor: c.tooltipBg,
   borderColor: c.tooltipBorder,
   borderWidth: 1,
   textStyle: { fontFamily, color: c.tooltipText, fontSize: 12 },
  },
 }
}

export function measureCategoryLabelWidth(categories: string[], fontSize = 12): number {
 if (typeof document === 'undefined' || !categories.length) return 160
 const canvas = document.createElement('canvas')
 const ctx = canvas.getContext('2d')
 if (!ctx) return 160
 ctx.font = `500 ${fontSize}px ${getAnalyticsFontFamily()}`
 const max = Math.max(...categories.map((c) => ctx.measureText(c).width))
 return Math.ceil(max) + 32
}

/** RTL-safe layout for horizontal bar charts (rankings). */
export function buildHorizontalBarLayout(
 categories: string[],
 rtl: boolean,
 colors: ReturnType<typeof getAnalyticsColors>,
) {
 const labelColumnWidth = measureCategoryLabelWidth(categories)
 const valueAxisPadding = 72

 return {
  labelColumnWidth,
  grid: {
   left: rtl ? valueAxisPadding : labelColumnWidth,
   right: rtl ? labelColumnWidth : valueAxisPadding,
   top: 16,
   bottom: 44,
   containLabel: true as const,
  },
  xAxis: {
   type: 'value' as const,
   ...(rtl ? { inverse: true } : {}),
   axisLine: { show: false },
   axisTick: { show: false },
   axisLabel: {
    color: colors.label,
    fontSize: 11,
    margin: 12,
    hideOverlap: true,
   },
   splitLine: { show: false },
  },
  yAxis: {
   type: 'category' as const,
   data: categories,
   inverse: true,
   position: (rtl ? 'right' : 'left') as 'left' | 'right',
   axisLine: { show: false },
   axisTick: { show: false },
   axisLabel: {
    color: colors.axisText,
    fontSize: 12,
    fontWeight: 500,
    width: Math.max(64, labelColumnWidth - 16),
    overflow: 'truncate' as const,
    ellipsis: '…',
    align: (rtl ? 'left' : 'right') as 'left' | 'right',
    margin: 8,
   },
  },
 }
}

export function buildAreaGradient(
 mode: AnalyticsThemeMode,
 color?: string,
): { type: 'linear'; x: number; y: number; x2: number; y2: number; colorStops: { offset: number; color: string }[] } {
 const c = getAnalyticsColors(mode)
 const base = color ?? c.primary
 return {
  type: 'linear',
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
   { offset: 0, color: `${base}40` },
   { offset: 1, color: `${base}05` },
  ],
 }
}

export function prefersReducedMotion(): boolean {
 if (typeof window === 'undefined') return false
 return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

type TooltipAxisItem = {
 marker?: string
 seriesName?: string
 name?: string
 value?: number | string | null
 axisValueLabel?: string
}

function escapeTooltipHtml(value: string): string {
 return value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
}

/** Split "4,999.96 ج.م" into RTL-safe HTML (number then currency). */
export function formatTooltipValueHtml(formatted: string): string {
 const trimmed = String(formatted ?? '').trim()
 const currencyMatch = trimmed.match(/^(-?[\d,]+(?:\.\d+)?)\s+(\S.+)$/)
 if (currencyMatch) {
  const amount = escapeTooltipHtml(currencyMatch[1])
  const currency = escapeTooltipHtml(currencyMatch[2])
  return (
   `<span dir="rtl" style="display:inline-flex;align-items:baseline;gap:4px;unicode-bidi:isolate;">` +
   `<span dir="ltr" style="unicode-bidi:isolate;font-weight:600;">${amount}</span>` +
   `<span style="font-weight:500;">${currency}</span>` +
   `</span>`
  )
 }
 return escapeTooltipHtml(trimmed)
}

function formatTooltipAxisValue(
 value: number | string | null | undefined,
 valueFormatter?: (value: number) => string,
): string {
 const num = Number(value ?? 0)
 if (valueFormatter) return formatTooltipValueHtml(valueFormatter(num))
 return escapeTooltipHtml(String(value ?? ''))
}

/** RTL axis tooltip: label on the right, value on the left, currency after number. */
export function buildAnalyticsAxisTooltip(
 tooltipBase: Record<string, unknown>,
 valueFormatter?: (value: number) => string,
 axisPointer: Record<string, unknown> = { type: 'cross' as const, crossStyle: { opacity: 0.35 } },
): EChartsOption['tooltip'] {
 return {
  ...tooltipBase,
  trigger: 'axis',
  renderMode: 'html',
  axisPointer,
  formatter: (params: unknown) => {
   const items = (Array.isArray(params) ? params : [params]) as TooltipAxisItem[]
   if (!items.length) return ''
   const header = escapeTooltipHtml(String(items[0].axisValueLabel ?? items[0].name ?? ''))
   const rows = items
    .map((item) => {
     const valueHtml = formatTooltipAxisValue(item.value, valueFormatter)
     const name = escapeTooltipHtml(String(item.seriesName ?? item.name ?? ''))
     return (
      `<div style="display:flex;align-items:center;gap:8px;margin-top:6px;line-height:1.45;">` +
      `<span style="flex:1;text-align:right;">${name}</span>` +
      `${item.marker ?? ''}` +
      `<span style="white-space:nowrap;">${valueHtml}</span>` +
      `</div>`
     )
    })
    .join('')
   return (
    `<div dir="rtl" style="text-align:right;min-width:9rem;font-family:${getAnalyticsFontFamily()};">` +
    `<div style="font-weight:600;margin-bottom:2px;">${header}</div>${rows}</div>`
   )
  },
 }
}

/** RTL item tooltip for pie/donut charts. */
export function buildAnalyticsItemTooltip(
 colors: ReturnType<typeof getAnalyticsColors>,
 valueFormatter?: (value: number) => string,
): EChartsOption['tooltip'] {
 return {
  confine: true,
  trigger: 'item',
  renderMode: 'html',
  backgroundColor: colors.tooltipBg,
  borderColor: colors.tooltipBorder,
  borderWidth: 1,
  textStyle: { fontFamily: getAnalyticsFontFamily(), color: colors.tooltipText, fontSize: 12 },
  formatter: (params: unknown) => {
   const item = params as TooltipAxisItem
   const name = escapeTooltipHtml(String(item.name ?? item.seriesName ?? ''))
   const valueHtml = formatTooltipAxisValue(item.value, valueFormatter)
   return (
    `<div dir="rtl" style="text-align:right;font-family:${getAnalyticsFontFamily()};">` +
    `<div style="display:flex;align-items:center;gap:8px;">` +
    `<span style="flex:1;">${name}</span>` +
    `${item.marker ?? ''}` +
    `<span style="white-space:nowrap;">${valueHtml}</span>` +
    `</div></div>`
   )
  },
 }
}
